/**
 * Accueil : le comparateur, en petit (sections/accueil-duel.liquid).
 * Plusieurs exemples : le suivant arrive toutes les « durée » secondes (fondu des raquettes, radar qui se
 * transforme en 500 ms) ; le bouton et l'exemple pointent toujours vers le comparateur pré-rempli avec la paire
 * affichée. Pause au survol de la carte, au focus, hors écran, onglet caché. À la première apparition, le radar
 * se déploie depuis le centre. « Réduire les animations » : rien ne bouge, premier exemple seulement.
 */
const reduction = window.matchMedia('(prefers-reduced-motion: reduce)');
const ease = (t) => 1 - Math.pow(1 - t, 3);
const lire = (s) => (s || '').trim().split(/\s+/).map((p) => p.split(',').map(Number));
const ecrire = (pts) => pts.map((p) => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');

class PsCmp extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.paires = [...this.querySelectorAll('.ps-cmp__pair')].map((p) => ({ a: lire(p.dataset.a), b: lire(p.dataset.b), lien: p.dataset.lien }));
    this.tuilesA = [...this.querySelectorAll('.ps-cmp__col--a .ps-cmp__rq')];
    this.tuilesB = [...this.querySelectorAll('.ps-cmp__col--b .ps-cmp__rq')];
    this.liens = [...this.querySelectorAll('[data-lien]')];
    this.polyA = this.querySelector('.ps-cmp__poly--a');
    this.polyB = this.querySelector('.ps-cmp__poly--b');
    this.index = 0;
    this.duree = (Number(this.dataset.duree) || 4.5) * 1000;
    this.pause = { survol: false, focus: false, hors: true, cache: document.hidden };
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.majPause('survol', true); }, { signal });
    this.addEventListener('pointerleave', () => this.majPause('survol', false), { signal });
    this.addEventListener('focusin', () => this.majPause('focus', true), { signal });
    this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.majPause('focus', false); }, { signal });
    document.addEventListener('visibilitychange', () => this.majPause('cache', document.hidden), { signal });
    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      const cta = e.target.closest && e.target.closest('.ps-cmp__cta');
      if (!cta) return;
      cta.classList.add('is-press');
      setTimeout(() => cta.classList.remove('is-press'), 1200);
    }, { signal, passive: true });

    const plusBas = this.getBoundingClientRect().top > window.innerHeight;
    // Radar replié tant que la carte n'est pas à l'écran : il se déploiera à son arrivée.
    if (plusBas && !reduction.matches && this.paires[0] && 'IntersectionObserver' in window) {
      this.polyA.setAttribute('points', ecrire(this.paires[0].a.map(() => [0, 0])));
      this.polyB.setAttribute('points', ecrire(this.paires[0].b.map(() => [0, 0])));
    }
    if ('IntersectionObserver' in window) {
      let premiere = true;
      this.observer = new IntersectionObserver((entries) => {
        const vu = entries[entries.length - 1].isIntersecting;
        if (vu && premiere) {
          premiere = false;
          if (plusBas && !reduction.matches && this.paires[0]) this.deployer();
        }
        this.majPause('hors', !vu);
      }, { threshold: 0.4 });
      this.observer.observe(this);
    } else {
      this.majPause('hors', false);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    clearTimeout(this.minuteur);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.ready = false;
  }

  tourne() {
    const p = this.pause;
    return this.paires.length > 1 && !reduction.matches && !p.survol && !p.focus && !p.hors && !p.cache;
  }

  majPause(cle, valeur) {
    this.pause[cle] = valeur;
    clearTimeout(this.minuteur);
    if (this.tourne()) this.minuteur = setTimeout(() => this.suivant(), this.duree);
  }

  animer(duree, pas) {
    if (this.raf) cancelAnimationFrame(this.raf);
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / duree);
      pas(ease(t));
      if (t < 1) this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  deployer() {
    const { a, b } = this.paires[this.index];
    this.animer(900, (t) => {
      this.polyA.setAttribute('points', ecrire(a.map(([x, y]) => [x * t, y * t])));
      this.polyB.setAttribute('points', ecrire(b.map(([x, y]) => [x * t, y * t])));
    });
  }

  suivant() {
    const k = (this.index + 1) % this.paires.length;
    const de = { a: lire(this.polyA.getAttribute('points')), b: lire(this.polyB.getAttribute('points')) };
    const vers = this.paires[k];
    this.index = k;
    this.tuilesA.forEach((t, j) => t.classList.toggle('is-on', j === k));
    this.tuilesB.forEach((t, j) => t.classList.toggle('is-on', j === k));
    this.liens.forEach((l) => l.setAttribute('href', vers.lien));
    const melange = (p, q, t) => q.map(([x, y], i) => [(p[i] ? p[i][0] : 0) + (x - (p[i] ? p[i][0] : 0)) * t, (p[i] ? p[i][1] : 0) + (y - (p[i] ? p[i][1] : 0)) * t]);
    this.animer(500, (t) => {
      this.polyA.setAttribute('points', ecrire(melange(de.a, vers.a, t)));
      this.polyB.setAttribute('points', ecrire(melange(de.b, vers.b, t)));
    });
    if (this.tourne()) this.minuteur = setTimeout(() => this.suivant(), this.duree);
  }
}

if (!customElements.get('ps-cmp')) customElements.define('ps-cmp', PsCmp);
